import { Theme } from '@mui/material/styles';

import { getProfileFieldsSx } from './profile-fields';

export const getDialogFieldsSx = (theme: Theme) => {
  const profile = getProfileFieldsSx(theme);
  return {
    '& .MuiFormControlLabel-root': profile['& .MuiFormControlLabel-root'],
    '& .MuiBox-root:has(> label.MuiFormLabel-root)': {
      gap: '5px',
      rowGap: '5px',
      minWidth: 0,
      '& > label.MuiFormLabel-root, & > label.MuiFormLabel-root *': {
        height: 'auto',
        margin: 0,
        fontSize: '12.5px',
        lineHeight: '18px',
        fontWeight: 400,
        letterSpacing: 0,
        textTransform: 'none',
        color: theme.custom?.headerInfoText,
      },
      '& > label.MuiFormLabel-root .MuiFormLabel-asterisk, & > label.MuiFormLabel-root .required':
        { color: theme.custom?.fieldRequiredMark, fontWeight: 600 },
      '& .MuiInputBase-root, & .MuiPickersInputBase-root': {
        width: '100%',
        margin: 0,
        borderRadius: '9px',
        background: theme.background.white,
        boxShadow: 'none',
        fontSize: '14px',
        fontWeight: 400,
        color: theme.custom?.brandNavy,
        transition: 'box-shadow 120ms',
        '&:not(.MuiInputBase-multiline)': { height: '38px !important' },
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${theme.custom?.fieldFocusRing}`,
        },
        '&.Mui-error': {
          boxShadow: `0 0 0 3px ${theme.custom?.fieldErrorRing}`,
        },
      },
      '&& .MuiInputBase-root.Mui-disabled, && .MuiPickersInputBase-root.Mui-disabled':
        {
          background: theme.custom?.columnsRowHoverBg,
          color: theme.custom?.iconButton,
        },
      '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline':
        {
          borderColor: `${theme.custom?.controlBorder} !important`,
          borderWidth: '1px !important',
          borderStyle: 'solid !important',
        },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline, & .Mui-focused .MuiPickersOutlinedInput-notchedOutline':
        { borderColor: `${theme.colors.primary.main} !important` },
      '& .Mui-error .MuiOutlinedInput-notchedOutline, & .Mui-error .MuiPickersOutlinedInput-notchedOutline':
        { borderColor: `${theme.custom?.fieldErrorBorder} !important` },
      '& .MuiInputBase-input': {
        height: 'auto',
        padding: '0 11px',
        lineHeight: '24px',
        '&::placeholder': {
          color: theme.custom?.fieldPlaceholder,
          opacity: 1,
        },
      },
      '& .MuiInputBase-adornedStart': { paddingLeft: '0 !important' },
      '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
        marginLeft: '11px',
        color: theme.custom?.tableHeaderText,
      },
      '& .MuiInputAdornment-positionStart + .MuiInputBase-input': {
        paddingLeft: '4px',
      },
      '& .MuiPickersInputBase-root': { paddingLeft: '0 !important' },
      '& .MuiPickersInputBase-sectionsContainer': {
        padding: '0 11px',
        lineHeight: '24px',
      },
      '& .MuiSelect-select': {
        minHeight: 'auto !important',
        lineHeight: '24px',
        paddingRight: '34px !important',
      },
      '& .MuiSelect-icon': { color: theme.custom?.iconButton },
      '& .MuiFormHelperText-root': {
        margin: '2px 0 0',
        fontSize: '11.5px',
      },
    },
  };
};

// `collapseAt` is the body width (px) at which it drops to one column; the
// 480px "add record" dialogs pass a smaller value so they keep 2 columns.
export const dialogGrid = (
  columns: number,
  gap = '14px',
  collapseAt = 560
) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap,
  alignItems: 'start',
  marginBottom: '14px',
  [`@container (max-width: ${collapseAt}px)`]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
});

export const getFlatCardSx = (theme: Theme) => ({
  '& > .MuiBox-root': {
    boxShadow: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    borderRadius: 0,
    overflow: 'visible',
  },
  '& > .MuiBox-root > .MuiBox-root:first-of-type': {
    margin: '0 0 8px',
    padding: 0,
    minHeight: 28,
    borderBottom: 'none',
  },
  '& > .MuiBox-root > .MuiBox-root:first-of-type > :first-of-type': {
    fontSize: '12px',
    fontWeight: 600,
    color: theme.custom?.headerInfoText,
  },
});

// Paper size of the 480px "add record" dialogs (Add status, Add journal
// entry, …); the 2-column ones with labor levels use 640px instead.
export const recordDialogSx = {
  '& .MuiDialog-paper': { width: '480px', maxWidth: 'calc(100% - 24px)' },
};

// A size container so dialogGrid can drop to one column.
export const getDialogBodySx = (theme: Theme) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto' as const,
  padding: '18px 22px 8px',
  containerType: 'inline-size',
  ...getDialogFieldsSx(theme),
});
